<script setup lang="ts">
import CopyButton from '@/components/common/CopyButton.vue'
import { DeleteConfirmationModal } from '@/components/common/DeleteConfirmationModal'
import TableSearchFilter from '@/components/common/TableSearchFilter.vue'
import RcShipmentSelect from '@/components/rc/common/RcShipmentSelect.vue'
import RcWorkOrderSelect from '@/components/rc/common/RcWorkOrderSelect.vue'
import { useContainers } from '@/composables/rc/containers'
import { getContainerStatuses } from '@/services/rc.service'
import { OrderBy } from '@/types/shared/filters'
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION_LAYOUT } from '@/types/shared/pagination'
import type { IContainerDashboard } from '@/types/rc/containers'
import type { IContainerDashboardFilters } from '@/types/rc/filters'
import { ShipmentType } from '@/types/rc/shipments'
import { Icon } from '@iconify/vue'
import type { OptionType } from 'element-plus/es/components/select-v2/src/select.types'
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { NotifyFail } from '@/helpers/core/notifications'
import type { TableColumnCtx } from 'element-plus'

const emit = defineEmits(['edit'])

const router = useRouter()

const selectedShipmentFilter = ref<{ id: number }>()
const selectedWorkOrderFilter = ref<any>()

const containerStatuses = ref<string[]>([])
const containerStatusFilterOptions = ref<OptionType[]>([])

watch([containerStatuses], () => {
  containerStatusFilterOptions.value = containerStatuses.value.map((status: string) => ({ key: status, value: status }))
})

const filters = reactive<IContainerDashboardFilters>({
  page: 1,
  per_page: DEFAULT_PAGE_SIZES[2],
  order_by: 'id',
  order_by_order: OrderBy.DESC,
  work_order_number: undefined,
  shipment_id: undefined,
  type: undefined,
  kind: undefined,
  status: undefined,
  fragment: undefined,
})

const filterSettings = [
  {
    key: 'kind',
    value: 'Shipment Type',
    options: [
      {
        key: ShipmentType.Inbound,
        value: ShipmentType.Inbound,
      },
      {
        key: ShipmentType.Outbound,
        value: ShipmentType.Outbound,
      },
    ],
    type: 'dropdown',
    placeholder: 'Shipment Type',
    modelValue: ref<string>(),
    onChange: (val: ShipmentType) => {
      filters.kind = val
    },
    iconClass: 'check-box-outline-blank',
    style: 'width: 208px',
  },
  {
    key: 'status',
    value: 'Status',
    options: containerStatusFilterOptions,
    type: 'dropdown',
    placeholder: 'Status',
    modelValue: ref<string>(),
    onChange: (val: string) => {
      filters.status = val
    },
    iconClass: 'label-outline-rounded',
    style: 'width: 208px',
  },
]

const searchSettings = {
  key: 'search',
  value: 'Search',
  type: 'search',
  placeholder: 'Container #',
  style: 'width: 256px',
  onChange: (val: string) => {
    if (val == '') {
      filters.fragment = undefined
    } else {
      filters.fragment = val
    }
  },
}

// Vue Query
const { getContainersQuery, deleteContainerMutation } = useContainers()
const { isFetching, isError, isRefetching, data, error, refetch } = getContainersQuery(filters)

// Compute message to display when table is empty.
const tableEmptyText = computed(() => {
  if (isFetching.value && !filters.fragment) return 'Loading...'
  if (isFetching.value && filters.fragment) return `Searching for ${filters.fragment}`
  if (filters.fragment && !isFetching.value) return `No results for "${filters.fragment}"`
  else return 'No data'
})

// DELETE mutation
const { mutateAsync: handleDelete } = deleteContainerMutation

/**
 * Handles row click, navigates to container details for a given row.
 *
 * @param row ElTable row.
 * @param column ElTable column.
 */
const handleRowClick = (row: any, column: any) => {
  if (column.columnKey !== 'actions') {
    router.push({ name: 'rc-logistics-container-details', params: { containerID: row.id } })
  }
}
/**
 * Gets the cell class name for each cell.
 * Applies "cursor: pointer" unless the cell is in the "actions" column.
 *
 * @param param0 destructured ElTable column.
 */
const getCellClassName = (data: {
  row: IContainerDashboard
  column: TableColumnCtx<IContainerDashboard>
  rowIndex: number
  columnIndex: number
}) => {
  if (data.column.columnKey !== 'actions') {
    return 'cursor-pointer'
  }

  return ''
}

/**
 * On mount: fetch container statuses.
 */
onMounted(async () => {
  try {
    containerStatuses.value = await getContainerStatuses()
  } catch {
    NotifyFail('Failed to fetch resources.')
  }
})

/**
 * Delete container handler. Stop bubbling to prevent reaching the row handler.
 * @param containerID the container number
 * @param e the click event
 */
const onClickDelete = (containerID: number, e: MouseEvent) => {
  e.stopPropagation()
  DeleteConfirmationModal(
    'Delete Container',
    'Are you sure you want to delete this container? This action cannot be undone',
    containerID,
    handleDelete
  )
}

/**
 * Edit container handler. Stop bubbling to prevent reaching the row handler.
 * @param container the container id
 * @param e the click event
 */
const onClickEdit = (container: IContainerDashboard, e: MouseEvent) => {
  e.stopPropagation()
  emit('edit', container)
}

const handleShipmentSelectChange = (val: { id: number } | undefined) => {
  filters.shipment_id = val?.id
}

const handleWorkOrderSelectChange = (val?: any) => {
  filters.work_order_number = val?.number
}
</script>

<template>
  <rl-error-result v-if="isError && !isRefetching" :error="error">
    <el-button @click="() => refetch()">
      <Icon icon="material-symbols:refresh-rounded" height="16" :inline="true" />&nbsp;Reload
    </el-button>
  </rl-error-result>
  <template v-else>
    <div class="containers-table__controls">
      <div class="containers-table__filters">
        <TableSearchFilter :search-settings="searchSettings" :disabled="isFetching" />
        <RcWorkOrderSelect
          v-model="selectedWorkOrderFilter"
          style="width: 256px"
          :disabled="isFetching"
          @change="handleWorkOrderSelectChange"
        />
        <RcShipmentSelect
          v-model="selectedShipmentFilter"
          style="width: 256px"
          :disabled="isFetching"
          @update:model-value="handleShipmentSelectChange"
        />
        <TableFilters :filters-list="filterSettings" />
      </div>
    </div>
    <el-table
      v-loading="isFetching"
      :data="data?.data"
      :empty-text="tableEmptyText"
      stripe
      :cell-class-name="getCellClassName"
      @row-click="handleRowClick"
    >
      <el-table-column prop="number" label="Container #" :min-width="176" />
      <el-table-column prop="work_order_number" label="Work Order #" :min-width="176">
        <template #default="scope">
          <copy-button :value="scope.row.work_order_number"> {{ scope.row.work_order_number }} </copy-button>
        </template>
      </el-table-column>
      <el-table-column label="Shipment" :min-width="80">
        <template #default="scope">
          <template v-if="scope.row.shipment">
            <el-tooltip effect="light" placement="bottom">
              <template #content>
                <p>ID #: {{ scope.row.shipment.id }}</p>
                <p v-if="scope.row.shipment.carrier">Carrier: {{ scope.row.shipment.carrier }}</p>
                <p>
                  <copy-button v-if="scope.row.shipment.tracking_number" :value="scope.row.shipment.tracking_number">
                    Tracking #: {{ scope.row.shipment.tracking_number }}
                  </copy-button>
                </p>
                <p>
                  <copy-button v-if="scope.row.shipment.waybill_number" :value="scope.row.shipment.waybill_number">
                    Waybill #: {{ scope.row.shipment.waybill_number }}
                  </copy-button>
                </p>
                <p>
                  <copy-button v-if="scope.row.shipment.bol_number" :value="scope.row.shipment.bol_number">
                    Bol #: {{ scope.row.shipment.bol_number }}
                  </copy-button>
                </p>
              </template>
              <el-button link>{{ scope.row.shipment.id }}</el-button>
            </el-tooltip>
          </template>
          <template v-else>{{ '-' }}</template>
        </template>
      </el-table-column>
      <el-table-column prop="kind" label="Shipment Type" :min-width="88" />
      <el-table-column prop="type" label="Type" :min-width="80" />
      <el-table-column label="Miners" :min-width="80">
        <template #default="scope">
          <el-tooltip effect="light" placement="bottom">
            <template #content>
              <p>{{ scope.row.assigned_miners }} assigned</p>
              <p>{{ scope.row.expected_miners }} expected</p>
            </template>
            <el-button link>{{ scope.row.assigned_miners }}/{{ scope.row.expected_miners }}</el-button>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column prop="status" label="Status" :min-width="88">
        <template #default="scope">
          <status-badge :status="scope.row.status" />
        </template>
      </el-table-column>
      <el-table-column :min-width="88" align="right">
        <template #default="scope">
          <el-button link title="Edit" @click="onClickEdit(scope.row as IContainerDashboard, $event)">
            <Icon icon="material-symbols:edit-outline" :height="16" />
          </el-button>
          <el-button link title="Delete" @click="onClickDelete(scope.row.id, $event)">
            <Icon icon="material-symbols:delete-outline" :height="16" />
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <el-pagination
      v-if="data && data.total_pages > 0"
      v-model:current-page="filters.page"
      v-model:page-size="filters.per_page"
      class="containers-table__pagination"
      :page-sizes="DEFAULT_PAGE_SIZES"
      :page-count="data.total_pages"
      :total="data.total_records"
      :disabled="isFetching"
      :layout="DEFAULT_PAGINATION_LAYOUT"
      background
    />
  </template>
</template>

<style scoped lang="scss">
.containers-table {
  &__filters {
    display: flex;
    gap: var(--spacing-spacing-sm);
    flex-wrap: wrap;
  }

  &__pagination {
    align-self: flex-end;
  }

  &__controls {
    display: inline-flex;
    justify-content: space-between;
  }
}
</style>
